/**
 * Ad attribution capture and storage for Meta Ads (fbclid, fbc, fbp) and UTM parameters.
 * Follows the same localStorage pattern as ReferralCapture in App.js.
 */

const FBCLID_KEY = 'latted_fbclid';
const FBC_KEY = 'latted_fbc';
const FBP_KEY = 'latted_fbp';
const UTM_KEY = 'latted_utm_params';

/**
 * Read a cookie by name.
 */
function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

/**
 * Set a cookie with domain-scoped path and expiry.
 */
function setCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax`;
}

/**
 * Generate a valid _fbp value in Meta's format: fb.1.{timestamp_ms}.{random10digits}
 * Used as a fallback when the Meta Pixel SDK hasn't set _fbp yet (ad blockers, timing).
 */
function generateFbp() {
  const rand = Math.floor(1000000000 + Math.random() * 9000000000);
  return `fb.1.${Date.now()}.${rand}`;
}

/**
 * Ensure _fbp exists. If Meta Pixel set it, use that. Otherwise create a fallback
 * and persist to both cookie and localStorage so CAPI always has a browser ID.
 */
function ensureFbp() {
  // Prefer the Pixel-set cookie
  const pixelFbp = getCookie('_fbp');
  if (pixelFbp) {
    localStorage.setItem(FBP_KEY, pixelFbp);
    return pixelFbp;
  }

  // Check localStorage for our fallback
  const stored = localStorage.getItem(FBP_KEY);
  if (stored) {
    // Re-set the cookie in case it expired
    setCookie('_fbp', stored, 90);
    return stored;
  }

  // Generate a new fallback
  const fbp = generateFbp();
  localStorage.setItem(FBP_KEY, fbp);
  setCookie('_fbp', fbp, 90);
  return fbp;
}

/**
 * Capture fbclid and UTM parameters from the current URL.
 * Stores them in localStorage/cookies and cleans the URL.
 * Safe to call on every route/search change — idempotent.
 */
export function captureAdAttribution() {
  const params = new URLSearchParams(window.location.search);
  let dirty = false;

  // Capture fbclid
  const fbclid = params.get('fbclid');
  if (fbclid) {
    localStorage.setItem(FBCLID_KEY, fbclid);
    // Generate _fbc cookie per Meta spec: fb.1.{timestamp_ms}.{fbclid}
    const fbc = `fb.1.${Date.now()}.${fbclid}`;
    localStorage.setItem(FBC_KEY, fbc);
    setCookie('_fbc', fbc, 90);
    params.delete('fbclid');
    dirty = true;
  }

  // Capture UTM parameters
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  const utmData = {};
  let hasUtm = false;
  for (const key of utmKeys) {
    const val = params.get(key);
    if (val) {
      utmData[key] = val;
      hasUtm = true;
      params.delete(key);
      dirty = true;
    }
  }
  if (hasUtm) {
    utmData._ts = Date.now();
    localStorage.setItem(UTM_KEY, JSON.stringify(utmData));
  }

  // Ensure _fbp always exists (create fallback if Pixel hasn't set it yet)
  ensureFbp();

  // Clean URL without reload (same pattern as ReferralCapture)
  if (dirty) {
    const url = new URL(window.location);
    url.search = params.toString();
    window.history.replaceState({}, '', url.pathname + (url.search ? url.search : ''));
  }
}

/**
 * Get all stored attribution data for sending to the backend.
 * @returns {{ fbclid?: string, fbc?: string, fbp?: string, utmSource?: string, utmMedium?: string, utmCampaign?: string, utmContent?: string, utmTerm?: string }}
 */
export function getAdAttribution() {
  const fbclid = localStorage.getItem(FBCLID_KEY) || undefined;
  const fbc = localStorage.getItem(FBC_KEY) || getCookie('_fbc') || undefined;
  // Use Pixel cookie, our persisted value, or ensure a fallback exists
  const fbp = getCookie('_fbp') || localStorage.getItem(FBP_KEY) || ensureFbp();

  // UTM params
  let utmSource, utmMedium, utmCampaign, utmContent, utmTerm;
  try {
    const raw = localStorage.getItem(UTM_KEY);
    if (raw) {
      const utm = JSON.parse(raw);
      utmSource = utm.utm_source;
      utmMedium = utm.utm_medium;
      utmCampaign = utm.utm_campaign;
      utmContent = utm.utm_content;
      utmTerm = utm.utm_term;
    }
  } catch { /* ignore parse errors */ }

  return { fbclid, fbc, fbp, utmSource, utmMedium, utmCampaign, utmContent, utmTerm };
}
